import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, FolderOpen, ArrowRight, Info, HardDrive, Database, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ConsistencyLogo from '@/components/ConsistencyLogo';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StorageConnect() {
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMode, setSelectedMode] = useState('temp');

  useEffect(() => {
    loadStatus();
    
    // Check for OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'connected') {
      toast.success('Dropbox connected successfully!');
      setSelectedMode('dropbox');
      window.history.replaceState({}, '', window.location.pathname);
      loadStatus();
    } else if (params.get('error')) {
      const error = params.get('error');
      const errorMessages = {
        'oauth_failed': 'Dropbox authorization failed',
        'no_code': 'No authorization code received',
        'connection_failed': 'Failed to establish connection',
        'invalid_state': 'Invalid session - please try again'
      };
      toast.error(errorMessages[error] || 'Connection error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/storage/status`, { withCredentials: true });
      setStatus(response.data);
      if (response.data.dropbox?.is_connected && response.data.dropbox?.is_active) {
        setSelectedMode('dropbox');
      }
    } catch (err) {
      console.error('Error loading storage status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectDropbox = () => {
    setConnecting(true);
    toast.info('Redirecting to Dropbox authorization...');
    // Redirect to OAuth - backend will redirect back here after auth
    window.location.href = `${API}/storage/dropbox/connect`;
  };

  const handleSelectTemp = async () => {
    try {
      await axios.post(`${API}/storage/settings`, { storage_mode: 'temp' }, { withCredentials: true });
      setSelectedMode('temp');
      toast.success('Temporary storage selected');
    } catch (err) {
      console.error('Error setting temp mode:', err);
    }
  };

  const handleFinish = async () => {
    setCompleting(true);
    try {
      // Set final storage mode if dropbox is connected
      if (selectedMode === 'dropbox' && status?.dropbox?.is_connected) {
        await axios.post(`${API}/storage/settings`, { storage_mode: 'dropbox' }, { withCredentials: true });
      }

      // Mark onboarding as complete
      const res = await fetch(`${API}/auth/complete-onboarding`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Failed to complete onboarding');
      }

      toast.success('Setup complete! Welcome to Consistency');
      
      // Clear session storage
      sessionStorage.removeItem('selected_plan');
      sessionStorage.removeItem('creator_name');
      sessionStorage.removeItem('creator_logo_preview');

      // Full page reload to refresh user state
      window.location.href = '/app/create';
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete setup');
    } finally {
      setCompleting(false);
    }
  };

  const dropboxConnected = status?.dropbox?.is_connected && status?.dropbox?.is_active;

  return (
    <div className="min-h-screen bg-[#121417] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="flex justify-center mb-6">
          <ConsistencyLogo variant="wordmark" size="md" />
        </div>

        <Card className="glass mb-6">
          <CardHeader>
            <CardTitle className="text-2xl text-white flex items-center gap-2">
              <FolderOpen className="h-6 w-6 text-blue-500" />
              Storage Configuration
            </CardTitle>
            <CardDescription className="text-[#8A93A6]">
              Choose where to store your content (you can change this later)
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
              </div>
            ) : (
              <>
                {/* Storage Options */}
                <div className="space-y-4">
                  {/* Dropbox Option */}
                  <div 
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      dropboxConnected 
                        ? 'bg-green-500/10 border-green-500/30' 
                        : selectedMode === 'dropbox' 
                          ? 'bg-blue-500/10 border-blue-500/30'
                          : 'bg-[#1A1D25] border-white/10 hover:border-white/20'
                    }`}
                    onClick={() => !dropboxConnected && handleConnectDropbox()}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <HardDrive className={`h-6 w-6 mt-0.5 ${dropboxConnected ? 'text-green-500' : 'text-blue-400'}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-medium">Dropbox Storage</h3>
                            {dropboxConnected && (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                          <p className="text-sm text-[#8A93A6] mb-3">
                            {dropboxConnected 
                              ? `Connected as ${status.dropbox.account_email || 'your account'}`
                              : 'Connect your Dropbox to permanently store all your content'}
                          </p>
                          {!dropboxConnected && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleConnectDropbox(); }}
                              disabled={connecting}
                              variant="outline"
                              size="sm"
                              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                            >
                              {connecting ? (
                                <>
                                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                                  Connecting...
                                </>
                              ) : (
                                'Connect Dropbox'
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Temp Storage Option */}
                  <div 
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedMode === 'temp' && !dropboxConnected
                        ? 'bg-cyan-500/10 border-cyan-500/30'
                        : 'bg-[#1A1D25] border-white/10 hover:border-white/20'
                    }`}
                    onClick={handleSelectTemp}
                  >
                    <div className="flex items-start gap-3">
                      <Database className={`h-6 w-6 mt-0.5 ${selectedMode === 'temp' && !dropboxConnected ? 'text-cyan-500' : 'text-gray-400'}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-medium">Temporary Storage</h3>
                          {selectedMode === 'temp' && !dropboxConnected && (
                            <CheckCircle className="h-4 w-4 text-cyan-500" />
                          )}
                        </div>
                        <p className="text-sm text-[#8A93A6]">
                          Files stored on our servers for 72 hours. No setup needed - connect Dropbox anytime later.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Alert */}
                <Alert className="border-blue-500/20 bg-blue-500/10">
                  <Info className="h-4 w-4 text-blue-500" />
                  <AlertDescription className="text-sm text-[#8A93A6]">
                    <strong className="text-white">Recommendation:</strong> Connect Dropbox for permanent storage and easy access to all your content from any device.
                  </AlertDescription>
                </Alert>

                {/* Finish Button */}
                <Button
                  onClick={handleFinish}
                  disabled={completing}
                  className="w-full bg-pink-500 hover:bg-pink-600"
                  size="lg"
                >
                  {completing ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Finishing Setup...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {dropboxConnected ? 'Continue with Dropbox' : 'Continue with Temp Storage'}
                      <ArrowRight className="h-5 w-5" />
                    </span>
                  )}
                </Button>

                <p className="text-xs text-center text-[#8A93A6]">
                  You can change storage settings anytime from Settings → Storage
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
